import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Zap, Globe, Loader2, Mail, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/streams");
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Please enter your email and password");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate("/streams");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background bg-grid">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-14 relative overflow-hidden border-r border-gray-800/60">
        <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/streamworship-mark.png" alt="StreamWorship" className="h-12 w-12 object-contain" />
          <div>
            <p className="font-display font-bold text-lg leading-none">Stream<span className="text-gold-gradient">Worship</span></p>
            <p className="text-xs text-gray-500 tracking-widest mt-1">BROADCAST CONTROL</p>
          </div>
        </div>

        <div className="relative">
          <motion.h1
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-4xl xl:text-5xl font-bold leading-tight tracking-tight"
          >
            Your streams.<br />
            <span className="text-gold-gradient">Total control.</span>
          </motion.h1>
          <p className="mt-6 text-gray-400 max-w-md leading-relaxed">
            Manage RTMP ingest endpoints, monitor live status, and control encoder keys from one premium broadcast dashboard.
          </p>
          <div className="mt-10 space-y-4">
            {[
              { icon: Zap, t: "Instant RTMP ingest", d: "Unique URL & key per stream" },
              { icon: ShieldCheck, t: "Role-based access", d: "Customer & Admin views" },
              { icon: Globe, t: "Global edge locations", d: "Low-latency delivery" },
            ].map((f, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-4"
              >
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
                  <f.icon className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{f.t}</p>
                  <p className="text-xs text-gray-500">{f.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <p className="relative text-xs text-gray-600">© 2026 StreamWorship · <a data-testid="login-privacy" href="/privacy" className="hover:text-gray-400 underline underline-offset-2">Privacy</a> · <a data-testid="login-terms" href="/terms" className="hover:text-gray-400 underline underline-offset-2">Terms</a> · <a data-testid="login-contact" href="/contact" className="hover:text-gray-400 underline underline-offset-2">Contact</a></p>
      </div>

      {/* Right auth panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md bg-card border border-amber-500/20 rounded-2xl p-8 sm:p-10 shadow-[0_0_60px_rgba(245,158,11,0.06)]"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/streamworship-mark.png" alt="StreamWorship" className="h-10 w-10 object-contain" />
            <p className="font-display font-bold text-lg">Stream<span className="text-gold-gradient">Worship</span></p>
          </div>

          <h2 className="font-display text-2xl font-bold">Welcome back</h2>
          <p className="text-gray-400 text-sm mt-2 mb-8">
            Sign in to access your broadcast control panel.
          </p>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input id="email" data-testid="login-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@church.org"
                  className="pl-10 bg-background border-gray-800" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <button type="button" data-testid="to-forgot-password" onClick={() => navigate("/forgot-password")} className="text-xs text-amber-400 hover:underline">Forgot password?</button>
              </div>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input id="password" data-testid="login-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="pl-10 bg-background border-gray-800" />
              </div>
            </div>

            <button
              data-testid="login-submit"
              type="submit"
              disabled={submitting}
              className="w-full gold-gradient hover:brightness-110 text-black font-semibold py-3.5 px-6 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20 transition-all cursor-pointer active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="text-sm text-gray-400 text-center mt-6">
            New organization? <button data-testid="to-register" onClick={() => navigate("/register")} className="text-amber-400 hover:underline">Create an account</button>
          </p>
          <p className="text-xs text-gray-500 text-center mt-3">
            <button data-testid="login-back-home" onClick={() => navigate("/")} className="hover:text-gray-300">← Back to home</button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
